// Activity icons, movement badges, and pick/entry rendering for Picks + Users screens.
import { ArrowDown, ArrowUp, Check, LogIn, UserPlus } from 'lucide-react';
import { cardMultiplier, getMarket, selectionLabel, type Movement, type RoundStatus } from '@h2h/shared';
import { Avatar } from '../../../components/ui/bits';
import { cn } from '../../../lib/cn';
import type { AdminCyclingPickRow, AdminEntryRow, AdminPredictionRow } from '../../../lib/adminApi';
import { timeAgo } from './format';
import { Pill, type PillTone } from './primitives';

export function ActivityIcon({ kind }: { kind: 'signup' | 'join' | 'pick' }) {
  const map = {
    signup: { Icon: UserPlus, cls: 'bg-accent-wash text-accent-ink' },
    join: { Icon: LogIn, cls: 'bg-night/[0.06] text-ink' },
    pick: { Icon: Check, cls: 'bg-gold-top/40 text-gold-ink' }
  } as const;
  const { Icon, cls } = map[kind];
  return (
    <span className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-full', cls)}>
      <Icon size={15} strokeWidth={2.4} />
    </span>
  );
}

export function MovementBadge({ movement }: { movement: Movement | null }) {
  if (!movement || movement === 'STAYED') return null;
  if (movement === 'PROMOTED')
    return (
      <Pill tone="accent">
        <ArrowUp size={11} strokeWidth={2.6} /> Up
      </Pill>
    );
  return (
    <Pill tone="danger">
      <ArrowDown size={11} strokeWidth={2.6} /> Down
    </Pill>
  );
}

function statusTone(status: RoundStatus): PillTone {
  if (status === 'OPEN') return 'accent';
  if (status === 'LOCKING' || status === 'SETTLING') return 'gold';
  return 'neutral';
}

export function RoundStatusPill({ status }: { status: RoundStatus }) {
  return <Pill tone={statusTone(status)}>{status}</Pill>;
}

/** A card's answers as readable chips (Build-Your-Card): the 1X2 base first (reads as
 *  the team name, or "Draw"), then one chip per stacked boost. An exact-score boost
 *  shows "Exact 2–1"; an anytime-scorer boost shows the player; every other boost as
 *  "<short> <opt>", e.g. "Clean sheet Yes", "Red card Yes". */
function pickChips(p: AdminPredictionRow): string[] {
  const teams = p.homeTeam && p.awayTeam ? { home: p.homeTeam, away: p.awayTeam } : undefined;
  const chips: string[] = [selectionLabel('outcome', p.outcome, teams)];
  for (const leg of p.legs) {
    const m = getMarket(leg.marketId);
    if (m?.kind === 'exact') chips.push(`Exact ${leg.selection.replace('-', '–')}`);
    else if (m?.kind === 'player') chips.push(leg.label ?? 'Scorer');
    else chips.push(m ? `${m.shortLabel} ${selectionLabel(leg.marketId, leg.selection, teams)}` : leg.selection);
  }
  return chips;
}

function PredictionLine({ p }: { p: AdminPredictionRow }) {
  const pts = p.cardScore;
  const mult = cardMultiplier(p.legs);
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-ink">{p.label}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {pickChips(p).map((chip, i) => (
            <Pill key={i} tone={i === 0 ? 'accent' : 'neutral'}>
              {chip}
            </Pill>
          ))}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {p.resultLabel && <span className="num text-[12px] font-semibold text-ink2">{p.resultLabel}</span>}
        <span className="num rounded-lg bg-night px-1.5 py-1 text-[11px] font-bold text-white">×{mult}</span>
        {pts != null && (
          <span
            className={cn(
              'num w-9 text-right text-[14px] font-extrabold',
              pts > 0 ? 'text-accent-ink' : pts < 0 ? 'text-miss' : 'text-ink3'
            )}
          >
            {pts}
          </span>
        )}
      </div>
    </div>
  );
}

export function EntryCard({ entry, showWho = true }: { entry: AdminEntryRow; showWho?: boolean }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-card p-4 shadow-tile">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {showWho && <Avatar name={entry.displayName} tone={entry.avatarTone} size={38} />}
          <div className="min-w-0">
            {showWho && <div className="truncate text-[14px] font-bold tracking-tight">{entry.displayName}</div>}
            <div className="truncate text-[12px] text-ink2">
              {entry.leagueName} · {entry.roundLabel}
            </div>
            <div className="text-[11px] text-ink3">{timeAgo(entry.submittedAt)}</div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {entry.roundScore != null ? (
            <>
              <div className="num text-[22px] font-extrabold leading-none">{entry.roundScore}</div>
              <div className="mt-1.5 flex items-center justify-end gap-1">
                {entry.rankInGroup != null && <Pill>#{entry.rankInGroup}</Pill>}
                <MovementBadge movement={entry.movement} />
              </div>
            </>
          ) : (
            <Pill tone={statusTone(entry.roundStatus)}>{entry.roundStatus}</Pill>
          )}
        </div>
      </div>
      <div className="mt-1 divide-y divide-black/5 border-t border-black/5">
        {entry.cyclingPicks && entry.cyclingPicks.length > 0
          ? entry.cyclingPicks.map((p) => <CyclingPickLine key={p.duelId} p={p} />)
          : entry.predictions.map((p) => <PredictionLine key={p.fixtureId} p={p} />)}
      </div>
    </div>
  );
}

/** One cycling duel: the matchup, the backed rider, confidence, and verdict.
 *  Mirrors PredictionLine but for the binary rider-vs-rider market. */
function CyclingPickLine({ p }: { p: AdminCyclingPickRow }) {
  const settled = p.outcome != null;
  const verdictTone: PillTone = !settled
    ? 'neutral'
    : p.outcome === 'void'
      ? 'gold'
      : p.correct
        ? 'accent'
        : 'danger';
  const verdictLabel = !settled ? null : p.outcome === 'void' ? 'Void' : p.correct ? 'Correct' : 'Wrong';
  const winnerName = p.winner === 'A' ? p.riderAName : p.winner === 'B' ? p.riderBName : null;
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-ink">
          {p.riderAName} <span className="text-ink3">v</span> {p.riderBName}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <Pill tone="dark">{p.pickedRiderName}</Pill>
          {verdictLabel && <Pill tone={verdictTone}>{verdictLabel}</Pill>}
          {settled && p.outcome !== 'void' && winnerName && (
            <span className="text-[11px] text-ink3">won: {winnerName}</span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="num rounded-lg bg-night px-1.5 py-1 text-[11px] font-bold text-white">×{p.confidenceWeight}</span>
        {p.points != null && (
          <span className={cn('num w-9 text-right text-[14px] font-extrabold', p.points > 0 ? 'text-accent-ink' : 'text-ink3')}>
            {p.points}
          </span>
        )}
      </div>
    </div>
  );
}
